import React, { type ReactNode } from "react";

// Brand marks + display metadata per LLM provider, used by the run-progress
// chips and the grounded activity log. SVGs are ported verbatim from the
// aits-2.0 prototype (03-virtual-buyer-run.html); they inherit `currentColor`,
// so colour comes from the `.openai`/`.gemini`/`.perplexity` CSS classes
// (--vb-model-* tokens). Unknown providers fall back to a neutral dot.

export interface ProviderMeta {
  /** CSS modifier on the chip/log row (drives the --vb-model-* background). */
  className: string;
  /** Human label for the provider family (brand name, not the model id). */
  label: string;
  logo: ReactNode;
}

const OPENAI_LOGO = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21.5 10.2a5.5 5.5 0 0 0-.47-4.52A5.6 5.6 0 0 0 15 3.07a5.5 5.5 0 0 0-9.3 1.96A5.5 5.5 0 0 0 2.5 13.8a5.5 5.5 0 0 0 .47 4.52A5.6 5.6 0 0 0 9 20.93a5.5 5.5 0 0 0 9.3-1.96 5.5 5.5 0 0 0 3.2-8.77zM12 19.3a4.1 4.1 0 0 1-2.63-.95l.13-.07 4.37-2.52a.71.71 0 0 0 .36-.62V9.01l1.85 1.07v5.05A4.12 4.12 0 0 1 12 19.3zM7.16 16.42a4.1 4.1 0 0 1-.49-2.76l.13.08 4.37 2.52a.71.71 0 0 0 .72 0l5.34-3.08v2.13l-4.42 2.55a4.12 4.12 0 0 1-5.65-1.44zM6 8.1a4.1 4.1 0 0 1 2.14-1.8v5.14a.71.71 0 0 0 .36.62l5.33 3.08-1.85 1.07-4.42-2.55A4.12 4.12 0 0 1 6 8.1zm14.78 3.44-5.34-3.1 1.85-1.06 4.42 2.55a4.11 4.11 0 0 1-.63 7.41v-5.18a.71.71 0 0 0-.3-.62zm1.84-2.77-.13-.08-4.36-2.54a.71.71 0 0 0-.72 0L12.27 9.2V7.07l4.42-2.55a4.11 4.11 0 0 1 6.1 4.26zM11.26 12.6 9.4 11.53V6.48a4.11 4.11 0 0 1 6.74-3.15l-.13.07-4.37 2.52a.71.71 0 0 0-.36.62zm1-2.17L14.65 9l2.38 1.37v2.74L14.65 14.5l-2.38-1.37z" />
  </svg>
);

const GEMINI_LOGO = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2c.4 5.3 4.7 9.6 10 10-5.3.4-9.6 4.7-10 10-.4-5.3-4.7-9.6-10-10 5.3-.4 9.6-4.7 10-10z" />
  </svg>
);

const PERPLEXITY_LOGO = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18M3 8h18M3 16h18" />
  </svg>
);

// Anthropic/Claude — the AI Trust Signal report fans across OpenAI/Claude/Gemini
// (the buyer pipeline uses Perplexity instead of Claude), so this entry exists
// for the report-side surfaces. A simple radiating burst, in the family style.
const ANTHROPIC_LOGO = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="3" x2="12" y2="21" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
    <line x1="18.4" y1="5.6" x2="5.6" y2="18.4" />
  </svg>
);

const FALLBACK_LOGO = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="12" r="6" />
  </svg>
);

const META: Record<string, ProviderMeta> = {
  OPENAI: { className: "openai", label: "ChatGPT", logo: OPENAI_LOGO },
  GEMINI: { className: "gemini", label: "Gemini", logo: GEMINI_LOGO },
  PERPLEXITY: { className: "perplexity", label: "Perplexity", logo: PERPLEXITY_LOGO },
  CLAUDE: { className: "anthropic", label: "Claude", logo: ANTHROPIC_LOGO },
  ANTHROPIC: { className: "anthropic", label: "Claude", logo: ANTHROPIC_LOGO },
};

/** Look up provider metadata by (case-insensitive) family key. Falls back to a
 *  neutral mark + the raw key as label so a new provider never breaks the UI. */
export function providerMeta(provider: string): ProviderMeta {
  const key = (provider || "").toUpperCase();
  return (
    META[key] ?? {
      className: "unknown",
      label: provider || "Provider",
      logo: FALLBACK_LOGO,
    }
  );
}

/** The three providers the buyer pipeline fans across - used to render a
 *  "Queued" placeholder row per provider before Redis seeds real legs. */
export const KNOWN_PROVIDERS = ["OPENAI", "GEMINI", "PERPLEXITY"] as const;
